// TanStack Query hooks for the Fatigue Management dashboard.
//
// Every section reads the same /admin/fatigue/summary response through
// useFatigueSummary, so the sections cannot disagree about the headline figure
// and switching section costs no request. The two drill-downs are separate
// queries because they are only fetched when a panel is actually opened.

import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { qk } from '../../../../../lib/queryKeys';
import { useClientScope } from '../../../../../shared/contexts/ClientScopeContext';
import type { ComparisonGroup } from '../../../../../shared/hooks/useComparisonGroups';
import { fatigueApi } from './fatigueApi';
import type { FatigueQueryParams } from './fatigueApi';
import { mapCohortDaily, mapIndividuals, mapSummary } from './fatigueMapper';
import { buildProjection } from './projection';
import type { FatigueFilters, FatigueSummary } from './types';

/** The generic filter state, in the shape `/admin/fatigue/*` reads server-side. */
const queryParams = (clientId: string | null, filters: FatigueFilters): FatigueQueryParams => ({
  region: filters.region.length > 0 ? filters.region : undefined,
  depot: filters.depot.length > 0 ? filters.depot : undefined,
  department: filters.department.length > 0 ? filters.department : undefined,
  job_role: filters.jobRole.length > 0 ? filters.jobRole : undefined,
  age_range: filters.ageRange.length > 0 ? filters.ageRange : undefined,
  sex: filters.sex.length > 0 ? filters.sex : undefined,
  shift_pattern: filters.shiftPattern.length > 0 ? filters.shiftPattern : undefined,
  user_ids: filters.searchUser.length > 0 ? filters.searchUser : undefined,
  reliable_data: filters.reliableDataOnly ? true : undefined,
  client_id: clientId,
});

export interface FatigueSummaryView extends FatigueSummary {
  /** Alias of `cohorts` — filtering happens server-side now, so nothing is dropped client-side. */
  visibleCohorts: FatigueSummary['cohorts'];
  projection: ReturnType<typeof buildProjection>;
}

const toView = (summary: FatigueSummary): FatigueSummaryView => ({
  ...summary,
  visibleCohorts: summary.cohorts,
  projection: buildProjection(summary.trend),
});

export const useFatigueSummary = (filters: FatigueFilters) => {
  const { effectiveClientId } = useClientScope();
  const params = queryParams(effectiveClientId, filters);

  const query = useQuery({
    queryKey: qk.fatigue.summary(params),
    queryFn: () => fatigueApi.getSummary(params),
    select: mapSummary,
  });

  const data = useMemo(() => (query.data ? toView(query.data) : undefined), [query.data]);

  return { ...query, data };
};

/**
 * One `/admin/fatigue/summary` fetch per comparison group (PEAK-263), mirroring
 * `useComparisonTrends`'s `useQueries` pattern — `useComparisonGroups` already
 * narrows each group's `filterParams` to one selected value of the active
 * dimension, so this only needs to re-shape that into `FatigueQueryParams`.
 */
export const useFatigueComparisonSummary = (groups: ComparisonGroup[]) => {
  const results = useQueries({
    queries: groups.map((group) => {
      const params: FatigueQueryParams = {
        region: group.filterParams.region,
        depot: group.filterParams.depot,
        department: group.filterParams.department,
        job_role: group.filterParams.job_role,
        age_range: group.filterParams.age_range,
        sex: group.filterParams.sex,
        shift_pattern: group.filterParams.shift_pattern,
        user_ids: group.filterParams.user_ids,
        reliable_data: group.filterParams.reliable_data,
        client_id: group.filterParams.client_id,
      };
      return {
        queryKey: qk.fatigue.summary(params),
        queryFn: () => fatigueApi.getSummary(params),
        select: mapSummary,
      };
    }),
  });

  return groups.map((group, index) => ({
    group,
    data: results[index]?.data ? toView(results[index].data as FatigueSummary) : undefined,
    isLoading: results[index]?.isLoading ?? false,
    isError: results[index]?.isError ?? false,
  }));
};

export const useCohortDaily = (cohortId: string | null, filters: FatigueFilters) => {
  const { effectiveClientId } = useClientScope();
  const params = queryParams(effectiveClientId, filters);

  const query = useQuery({
    queryKey: qk.fatigue.cohortDaily(cohortId, params),
    queryFn: () => fatigueApi.getCohortDaily(cohortId as string, params),
    enabled: Boolean(cohortId),
  });

  return {
    ...query,
    series: query.data ? mapCohortDaily(query.data) : undefined,
    cohort: query.data?.cohort,
    suppressed: query.data?.suppressed ?? false,
  };
};

/**
 * Individual-level detail (FM-8/FM-9). `enabled` is the FM-10 gate — the API
 * enforces the same rule, so this only avoids a request that would 403.
 */
export const useCohortIndividuals = (cohortId: string | null, filters: FatigueFilters, enabled: boolean) => {
  const { effectiveClientId } = useClientScope();
  const params = queryParams(effectiveClientId, filters);

  const query = useQuery({
    queryKey: qk.fatigue.cohortIndividuals(cohortId, params),
    queryFn: () => fatigueApi.getCohortIndividuals(cohortId as string, params),
    enabled: Boolean(cohortId) && enabled,
  });

  return {
    ...query,
    individuals: query.data ? mapIndividuals(query.data) : undefined,
    window: query.data?.window,
  };
};
